import { FormattedMessage } from 'react-intl';
import { Row, Col, UncontrolledTooltip } from 'reactstrap';
import { format } from 'date-fns';
import ReactTooltip from 'react-tooltip';
import { useMsal } from '@azure/msal-react';

import Info from 'src/componentes/info';
import formatDateFns from 'src/utils/formatDateFns';
import {
  DownloadArquivoSistema,
  DownloadFileBucket,
  DownloadArquivoSharepoint,
} from 'src/services/downloadFileUrl';
import Avatar from 'src/componentes/avatar';
import ContentManager from 'src/componentes/content-manager';
import ViewDocument from '../ViewDocument';
import useAuth from 'src/hooks/useAuth';
import './dropzone-arquivo-exibicao-tabela.css';
import LucideIcon from 'src/componentes/lucide-icon';

const handleDownloadArquivo = (
  arquivo,
  isFileBucket,
  instanceSharepoint,
  convertWordToPdf = false,
  urlCustom = null,
  parameters = {}
) => {
  if (isFileBucket) {
    DownloadFileBucket(arquivo.nome, convertWordToPdf, urlCustom, parameters);
  } else if (arquivo.ehArquivoSharepoint) {
    DownloadArquivoSharepoint(arquivo, instanceSharepoint);
  } else if (arquivo.guid) {
    DownloadArquivoSistema(arquivo.guid);
  } else if (arquivo.id) {
    DownloadArquivoSistema(arquivo.id);
  }
};

const handleViewDocument = (
  arquivoNome,
  onlyRead,
  convertWordToPdf,
  documentName,
  documentType,
  urlCustom = null,
  parameters = {}
) => {
  ContentManager.addContent(
    <ViewDocument
      fileName={arquivoNome}
      onlyRead={onlyRead}
      convertWordToPdf={convertWordToPdf}
      urlCustom={urlCustom}
      parameters={parameters}
      documentName={documentName}
      documentType={documentType}
    />
  );
};

const isPdfFile = (fileName, convertWordToPdf) => {
  return (
    typeof fileName === 'string' &&
    (fileName.toLowerCase().endsWith('.pdf') ||
      (convertWordToPdf &&
        (fileName.toLowerCase().endsWith('.docx') ||
          fileName.toLowerCase().endsWith('.doc'))))
  );
};

const DropzoneArquivoExibicaoTabela = ({
  arquivos = [],
  onDeleteFile,
  isFileBucket = false,
  disabled = false,
  disabledDownload = null,
  isOnlyDownload = false,
  onlyRead = false,
  convertWordToPdf = false,
  urlCustom = null,
  documentName = null,
  documentType = null,
  parameters = {},
}) => {
  const { user } = useAuth();
  const { instance } = useMsal();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma);
  const renderDate = (dataInfo) => {
    if (!dataInfo) return '-';
    if (typeof dataInfo === 'string') return dataInfo;

    return format(dataInfo, currentDisplay, { locale: currentLocale });
  };

  const tooltipId = (arquivo, idx) => {
    const base = arquivo.id ?? idx;
    return `iconTipoArquivoSistema-${String(base)}`;
  };

  return (
    <div className="dropzone-anexo">
      <table className="dropzone-table">
        <thead>
          <tr>
            <th></th>
            <th>
              <FormattedMessage id="nome" />
            </th>
            <th>
              <FormattedMessage id="inseridoPor" />
              <Info
                id="responsavelAnexoHint"
                className="tabela-hint"
                placement="right"
                text={<FormattedMessage id="responsavelAnexoHint" />}
              />
            </th>
            <th>
              <FormattedMessage id="data" />
              <Info
                id="dataInclusaolAnexoHint"
                className="tabela-hint"
                placement="right"
                text={<FormattedMessage id="dataInclusaolAnexoHint" />}
              />
            </th>
            <th style={{ width: '60px' }} />
          </tr>
        </thead>
        <tbody>
          {arquivos.map((arquivo, idx) => {
            const rowClass = arquivo.isNew ? 'new-row' : '';
            const isDocumentView = isPdfFile(arquivo.nome, convertWordToPdf);
            const isDownloadDisabled = disabledDownload ?? disabled;
            return (
              <tr key={arquivo.id || idx} className={rowClass}>
                <td>
                  {arquivo.ehArquivoSharepoint ? (
                    <LucideIcon
                      icon="Link"
                      data-tip
                      data-for={tooltipId(arquivo, idx)}
                      size={15}
                      aria-describedby={tooltipId(arquivo, idx)}
                    />
                  ) : (
                    <LucideIcon icon="FileText" size={15} />
                  )}

                  {arquivo.ehArquivoSharepoint && (
                    <ReactTooltip
                      id={tooltipId(arquivo, idx)}
                      place="top"
                      aria-haspopup="true"
                      effect="solid"
                    >
                      <div
                        style={{
                          maxWidth: 300,
                          textAlign: 'justify',
                          whiteSpace: 'normal',
                        }}
                      >
                        {arquivo.caminhoArquivo}
                      </div>
                    </ReactTooltip>
                  )}
                </td>

                <td>{arquivo.nomeOriginal ?? arquivo.nome ?? '-'}</td>
                <td className="text-center">
                  {arquivo.responsavelNome ? (
                    <Row className="align-items-center">
                      <Col xs="auto">
                        <Avatar
                          size={32}
                          foto={arquivo.fotoColaborador}
                          label={arquivo.responsavelNome}
                          showLabel={true}
                        />
                      </Col>
                    </Row>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {arquivo.dataInclusao
                    ? renderDate(arquivo.dataInclusao)
                    : '-'}
                </td>
                <td>
                  <div className="d-flex justify-content-center">
                    {isFileBucket && !arquivo.isNew && (
                      <div id={`btnViewDocument-${idx}`}>
                        <button
                          disabled={!isDocumentView}
                          type="button"
                          className="btn btn-link p-0 mr-2"
                          onClick={() =>
                            handleViewDocument(
                              arquivo.nome,
                              isDocumentView && isDownloadDisabled,
                              convertWordToPdf,
                              documentName,
                              documentType,
                              urlCustom,
                              { ...parameters, isDownload: false }
                            )
                          }
                        >
                          <LucideIcon icon="eye-outline" />
                        </button>
                        <UncontrolledTooltip
                          placement="bottom"
                          target={`btnViewDocument-${idx}`}
                        >
                          {isDocumentView || convertWordToPdf ? (
                            <FormattedMessage id="visualizarDocumento" />
                          ) : (
                            <FormattedMessage id="formatoNaoCompativelVisualizacao" />
                          )}
                        </UncontrolledTooltip>
                      </div>
                    )}
                    {!arquivo.isNew && (
                      <button
                        disabled={isDownloadDisabled}
                        type="button"
                        className="btn btn-link p-0 mr-2"
                        onClick={() =>
                          handleDownloadArquivo(
                            arquivo,
                            isFileBucket,
                            instance,
                            convertWordToPdf,
                            urlCustom,
                            { ...parameters, isDownload: true }
                          )
                        }
                      >
                        <LucideIcon icon="download" />
                      </button>
                    )}
                    <button
                      disabled={disabled || isOnlyDownload || onlyRead}
                      style={{
                        display:
                          disabled || isOnlyDownload || onlyRead
                            ? 'none'
                            : 'flex',
                      }}
                      type="button"
                      className="btn btn-link p-0"
                      onClick={() => onDeleteFile?.(arquivo)}
                    >
                      <LucideIcon icon="delete" color="red" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DropzoneArquivoExibicaoTabela;
